import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import api, { formatApiError, formatDate, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Users, Ticket as TicketIcon, Megaphone, Tag as TagIcon, FilmStrip, FileText, Truck, ChartBar } from "@phosphor-icons/react";
import { ImageUploadField, ImageGalleryField } from "@/components/ImageUpload";
import { MarketplaceImportBar } from "@/components/MarketplaceImportBar";

const TABS = [
  { id: "stats", label: "Overview", icon: ChartBar },
  { id: "members", label: "Members", icon: Users },
  { id: "tickets", label: "Tickets", icon: TicketIcon },
  { id: "products", label: "Products", icon: TagIcon },
  { id: "videos", label: "Videos", icon: FilmStrip },
  { id: "downloads", label: "Downloads", icon: FileText },
  { id: "inventory", label: "Inventory", icon: Truck },
  { id: "announcements", label: "Announcements", icon: Megaphone },
];

export default function Admin() {
  const [tab, setTab] = useState("stats");
  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-10">
        <div className="section-label mb-3">— ADMIN CONTROL ROOM</div>
        <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight mb-8" data-testid="admin-headline">Operations Console</h1>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#262626] pb-4" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 font-chivo font-bold text-xs uppercase tracking-wider border ${tab === t.id ? "bg-[#00D4FF] border-[#00D4FF] text-black" : "border-[#262626] text-white hover:border-[#404040]"}`}
            >
              <t.icon size={14} weight="bold" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "stats" && <StatsTab />}
        {tab === "members" && <MembersTab />}
        {tab === "tickets" && <TicketsTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "videos" && <VideosTab />}
        {tab === "downloads" && <DownloadsTab />}
        {tab === "inventory" && <InventoryTab />}
        {tab === "announcements" && <AnnouncementsTab />}
      </div>
    </Layout>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch((err) => console.error("Failed to load admin stats:", err));
  }, []);
  if (!stats) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;
  const cards = [
    { label: "Total Members", value: stats.total_members, color: "text-white" },
    { label: "Active Memberships", value: stats.active_members, color: "text-[#00D4FF]" },
    { label: "Open Tickets", value: stats.open_tickets, color: "text-[#FF5722]" },
    { label: "Revenue (Paid)", value: formatMoney(stats.revenue), color: "text-[#00D4FF]" },
    { label: "Expiring (30d)", value: stats.expiring_soon, color: "text-white" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-[#262626] border border-[#262626]" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.label} className="bg-[#0A0A0A] p-6">
          <div className="font-mono text-[10px] text-[#9CA3AF] tracking-widest uppercase mb-3">{c.label}</div>
          <div className={`font-mono font-bold text-3xl lg:text-4xl ${c.color}`}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function MembersTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/members");
      setMembers(data);
    } catch (err) {
      console.error("Failed to load members:", err);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/members/${id}`, { status }); toast.success("Updated"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const extend = async (id, days) => {
    try { await api.post(`/admin/members/${id}/extend?days=${days}`); toast.success(`Extended ${days} days`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;
  return (
    <div className="surface overflow-x-auto" data-testid="admin-members-table">
      <table className="w-full text-sm">
        <thead className="bg-[#0A0A0A]">
          <tr className="font-mono text-[10px] text-[#9CA3AF] uppercase tracking-widest">
            <th className="text-left p-4">Name / Email</th>
            <th className="text-left p-4">Company</th>
            <th className="text-left p-4">Status</th>
            <th className="text-left p-4">Expires</th>
            <th className="text-left p-4">Auto-Renew</th>
            <th className="text-right p-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} className="border-t border-[#262626]" data-testid={`member-row-${m.id}`}>
              <td className="p-4">
                <div className="font-chivo font-bold text-white">{m.name}</div>
                <div className="font-mono text-xs text-[#52525B]">{m.email}</div>
              </td>
              <td className="p-4 text-[#9CA3AF]">{m.company || "—"}</td>
              <td className="p-4">
                <select value={m.membership_status || "inactive"} onChange={(e) => updateStatus(m.id, e.target.value)} className="input-rugged text-xs py-1" data-testid={`status-select-${m.id}`}>
                  <option value="inactive">Inactive</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="expired">Expired</option>
                </select>
              </td>
              <td className="p-4 font-mono text-xs text-[#9CA3AF]">{m.membership_expires_at ? formatDate(m.membership_expires_at) : "—"}</td>
              <td className="p-4 font-mono text-xs">{m.auto_renew ? <span className="text-[#00D4FF]">ON</span> : <span className="text-[#52525B]">OFF</span>}</td>
              <td className="p-4 text-right space-x-2">
                <button onClick={() => extend(m.id, 365)} className="tag tag-active hover:bg-[#121212]" data-testid={`extend-1y-${m.id}`}>+1Y</button>
                <button onClick={() => extend(m.id, 30)} className="tag hover:border-[#404040]" data-testid={`extend-30d-${m.id}`}>+30D</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TicketsTab() {
  const [tickets, setTickets] = useState([]);
  useEffect(() => {
    api.get("/tickets")
      .then((r) => setTickets(r.data))
      .catch((err) => console.error("Failed to load admin tickets:", err));
  }, []);
  return (
    <div className="surface divide-y divide-[#262626]" data-testid="admin-tickets">
      {tickets.length === 0 && <div className="p-12 text-center font-mono text-xs text-[#52525B]">No tickets.</div>}
      {tickets.map((t) => (
        <a href={`/support/${t.id}`} key={t.id} className="block p-5 hover:bg-[#121212]" data-testid={`admin-ticket-${t.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`tag ${t.status === "open" ? "tag-active" : ""}`}>{t.status}</span>
                <span className="tag">{t.priority}</span>
                <span className="font-mono text-xs text-[#52525B]">{t.category}</span>
              </div>
              <div className="font-chivo font-bold text-white">{t.subject}</div>
              <div className="font-mono text-xs text-[#52525B] mt-1">{t.user_name} · {t.user_email}</div>
            </div>
            <div className="font-mono text-xs text-[#52525B]">{formatDate(t.updated_at)}</div>
          </div>
        </a>
      ))}
    </div>
  );
}

function CrudTab({ resource, fields, testIdPrefix, marketplaceImport = false }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/${resource}`);
      setItems(data);
    } catch (err) {
      console.error(`Failed to load ${resource}:`, err);
    }
  };
  useEffect(() => { load(); }, [resource]);

  const remove = async (id) => {
    if (!confirm("Delete this item?")) return;
    try { await api.delete(`/${resource}/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const save = async (data) => {
    try {
      if (editing && editing.id) {
        await api.put(`/${resource}/${editing.id}`, data);
      } else {
        await api.post(`/${resource}`, data);
      }
      toast.success("Saved");
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary" data-testid={`${testIdPrefix}-new-btn`}>
          <Plus size={14} weight="bold" /> New
        </button>
      </div>
      {showForm && (
        <CrudForm
          fields={fields}
          initial={editing || {}}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={save}
          testIdPrefix={testIdPrefix}
          marketplaceImport={marketplaceImport}
        />
      )}
      <div className="surface divide-y divide-[#262626]" data-testid={`${testIdPrefix}-list`}>
        {items.length === 0 && <div className="p-12 text-center font-mono text-xs text-[#52525B]">No items.</div>}
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between p-4" data-testid={`${testIdPrefix}-row-${it.id}`}>
            <div className="flex-1 min-w-0 pr-4">
              <div className="font-chivo font-bold text-white truncate">{it.title || it.name}</div>
              <div className="font-mono text-xs text-[#52525B]">{it.category} {it.msrp_price ? `· MSRP ${formatMoney(it.msrp_price)} · Member ${formatMoney(it.member_price)}` : ""}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditing(it); setShowForm(true); }} className="tag hover:border-[#00D4FF]" data-testid={`${testIdPrefix}-edit-${it.id}`}>
                <PencilSimple size={12} weight="bold" />
              </button>
              <button onClick={() => remove(it.id)} className="tag hover:border-[#EF4444] hover:text-[#EF4444]" data-testid={`${testIdPrefix}-delete-${it.id}`}>
                <Trash size={12} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CrudForm({ fields, initial, onCancel, onSave, testIdPrefix, marketplaceImport = false }) {
  const renderInitialValue = (fd) => {
    if (initial[fd.key] !== undefined && initial[fd.key] !== null) return initial[fd.key];
    if (fd.type === "checkbox") return false;
    if (fd.type === "number") return 0;
    if (fd.type === "images") return [];
    return "";
  };
  const [form, setForm] = useState(() => {
    const f = {};
    fields.forEach((fd) => { f[fd.key] = renderInitialValue(fd); });
    return f;
  });

  const applyImport = (imported) => {
    const next = { ...form };
    fields.forEach((fd) => {
      if (imported[fd.key] !== undefined && imported[fd.key] !== null && imported[fd.key] !== "") {
        next[fd.key] = imported[fd.key];
      }
    });
    setForm(next);
  };
  const submit = (e) => {
    e.preventDefault();
    const payload = {};
    fields.forEach((fd) => {
      let v = form[fd.key];
      if (fd.type === "number") v = parseFloat(v) || 0;
      payload[fd.key] = v;
    });
    onSave(payload);
  };
  const renderField = (fd) => {
    const testId = `${testIdPrefix}-field-${fd.key}`;
    const setValue = (v) => setForm({ ...form, [fd.key]: v });
    if (fd.type === "textarea") {
      return <textarea value={form[fd.key]} onChange={(e) => setValue(e.target.value)} rows={3} className="input-rugged resize-none" data-testid={testId} />;
    }
    if (fd.type === "select") {
      return (
        <select value={form[fd.key]} onChange={(e) => setValue(e.target.value)} className="input-rugged" data-testid={testId}>
          {fd.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (fd.type === "checkbox") {
      return (
        <label className="flex items-center gap-2 input-rugged cursor-pointer">
          <input type="checkbox" checked={!!form[fd.key]} onChange={(e) => setValue(e.target.checked)} data-testid={testId} />
          <span className="font-plex text-sm">{form[fd.key] ? "Yes" : "No"}</span>
        </label>
      );
    }
    if (fd.type === "image") {
      return <ImageUploadField value={form[fd.key]} onChange={setValue} purpose={fd.purpose || "inventory_image"} testId={testId} />;
    }
    if (fd.type === "images") {
      return <ImageGalleryField value={form[fd.key]} onChange={setValue} purpose={fd.purpose || "inventory_image"} testId={testId} />;
    }
    const inputType = fd.type === "number" ? "number" : "text";
    return <input type={inputType} step="0.01" value={form[fd.key]} onChange={(e) => setValue(e.target.value)} className="input-rugged" data-testid={testId} />;
  };

  return (
    <form onSubmit={submit} className="surface p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`${testIdPrefix}-form`}>
      {marketplaceImport && (
        <MarketplaceImportBar onImport={applyImport} testIdPrefix={`${testIdPrefix}-marketplace-import`} />
      )}
      {fields.map((fd) => (
        <div key={fd.key} className={fd.full ? "md:col-span-2" : ""}>
          <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">{fd.label}</label>
          {renderField(fd)}
        </div>
      ))}
      <div className="md:col-span-2 flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary" data-testid={`${testIdPrefix}-save`}>Save</button>
      </div>
    </form>
  );
}

const productFields = [
  { key: "name", label: "Name", type: "text" },
  { key: "sku", label: "SKU", type: "text" },
  { key: "category", label: "Category", type: "text" },
  { key: "images", label: "Image Gallery (first is primary)", type: "images", full: true, purpose: "product_image" },
  { key: "youtube_url", label: "YouTube Product / Demo Video URL", type: "text", full: true },
  { key: "description", label: "Description", type: "textarea", full: true },
  { key: "msrp_price", label: "MSRP Price", type: "number" },
  { key: "member_price", label: "Member Price", type: "number" },
  { key: "in_stock", label: "In Stock", type: "checkbox" },
];
function ProductsTab() { return <CrudTab resource="products" fields={productFields} testIdPrefix="products" marketplaceImport />; }

const videoFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "category", label: "Category", type: "text" },
  { key: "youtube_url", label: "YouTube Embed URL", type: "text", full: true },
  { key: "storage_path", label: "Direct Video URL (storage)", type: "text", full: true },
  { key: "thumbnail_url", label: "Thumbnail URL", type: "text", full: true },
  { key: "duration", label: "Duration", type: "text" },
  { key: "featured", label: "Featured", type: "checkbox" },
  { key: "description", label: "Description", type: "textarea", full: true },
];
function VideosTab() { return <CrudTab resource="videos" fields={videoFields} testIdPrefix="videos" />; }

const downloadFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "category", label: "Category", type: "text" },
  { key: "file_path", label: "File Path / Storage Key", type: "text" },
  { key: "file_name", label: "File Name", type: "text" },
  { key: "description", label: "Description", type: "textarea", full: true },
];
function DownloadsTab() { return <CrudTab resource="downloads" fields={downloadFields} testIdPrefix="downloads" />; }

const inventoryFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "category", label: "Category (Used/Demo/Refurbished)", type: "text" },
  { key: "condition", label: "Condition", type: "text" },
  { key: "hours", label: "Hours", type: "text" },
  { key: "msrp_price", label: "MSRP", type: "number" },
  { key: "member_price", label: "Member Price", type: "number" },
  { key: "images", label: "Image Gallery (first is primary)", type: "images", full: true, purpose: "inventory_image" },
  { key: "youtube_url", label: "YouTube Walkaround / Demo Video URL", type: "text", full: true },
  { key: "released_at", label: "Public Release Date (ISO)", type: "text", full: true },
  { key: "description", label: "Description", type: "textarea", full: true },
  { key: "sold", label: "Sold", type: "checkbox" },
];
function InventoryTab() { return <CrudTab resource="inventory" fields={inventoryFields} testIdPrefix="inventory" marketplaceImport />; }

const announcementFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "pinned", label: "Pinned", type: "checkbox" },
  { key: "body", label: "Body", type: "textarea", full: true },
];
function AnnouncementsTab() { return <CrudTab resource="announcements" fields={announcementFields} testIdPrefix="announcements" />; }
